/**
 * Tutor compose for pod messages, from the inbox rather than the pod page.
 *
 * Reach within ONE pod: one student, several students, a group, or that pod's whole cohort. A tutor
 * who belongs to several pods picks exactly one to write to.
 *
 * There is deliberately no "all my pods" option and no cross-pod send path. Communication stays
 * inside a single pod; do not add a fan-out that posts one composed message to several pods.
 */
import { useState, useEffect, useCallback } from 'react'
import { X, Send, Eye } from 'lucide-react'
import toast from 'react-hot-toast'
import api from '../../services/api'
import podMessagingService from '../../services/podMessagingService'

export default function PodComposeModal({ isOpen, pods, onClose, onSent }) {
  const [scope, setScope] = useState('') // the single pod being written to
  const [audienceType, setAudienceType] = useState('cohort')
  const [groups, setGroups] = useState([])
  const [classes, setClasses] = useState([])
  const [students, setStudents] = useState([])
  const [groupId, setGroupId] = useState('')
  const [classId, setClassId] = useState('')
  const [targetIds, setTargetIds] = useState([])
  const [subject, setSubject] = useState('')
  const [content, setContent] = useState('')
  const [sending, setSending] = useState(false)

  // Default to the tutor's only pod, or leave unset so they must choose.
  useEffect(() => {
    if (!isOpen) return
    setScope(pods.length === 1 ? pods[0].id : '')
    setAudienceType('cohort')
    setGroupId('')
    setClassId('')
    setTargetIds([])
    setSubject('')
    setContent('')
  }, [isOpen, pods])

  const loadPodRoster = useCallback(async (orgId) => {
    if (!orgId) {
      setGroups([])
      setClasses([])
      setStudents([])
      return
    }
    try {
      const [groupRes, podRes, classRes] = await Promise.all([
        api.get('/student-groups', { params: { organizationId: orgId } }),
        api.get(`/tutor-groups/${orgId}`),
        api.get('/pod-events/classes', { params: { organizationId: orgId } }).catch(() => null)
      ])
      setGroups(groupRes.data?.data?.groups || [])
      setClasses(classRes?.data?.data?.classes || [])
      setStudents(
        (podRes.data?.data?.students || [])
          .filter((s) => s.status === 'active')
          .map((s) => ({
            id: s.user.id,
            name: `${s.user.firstName || ''} ${s.user.lastName || ''}`.trim() || s.user.email
          }))
      )
    } catch {
      setGroups([])
      setStudents([])
    }
  }, [])

  useEffect(() => {
    loadPodRoster(scope)
    setGroupId('')
    setClassId('')
    setTargetIds([])
  }, [scope, loadPodRoster])

  if (!isOpen) return null

  const toggleTarget = (id) =>
    setTargetIds((prev) => (prev.includes(id) ? prev.filter((x) => x !== id) : [...prev, id]))

  const canSend =
    !sending &&
    Boolean(scope) &&
    Boolean(content.trim()) &&
    (audienceType === 'cohort' ||
      (audienceType === 'group' && groupId) ||
      (audienceType === 'class' && classId) ||
      (audienceType === 'students' && targetIds.length > 0))

  const submit = async (e) => {
    e.preventDefault()
    setSending(true)
    try {
      await podMessagingService.createThread({
        organizationId: scope,
        audienceType,
        studentGroupId: audienceType === 'group' ? groupId : undefined,
        classId: audienceType === 'class' ? classId : undefined,
        targetIds: audienceType === 'students' ? targetIds : undefined,
        subject,
        content
      })
      toast.success('Message sent.')
      onSent()
    } catch (err) {
      toast.error(err?.response?.data?.error?.message || 'Could not send the message.')
    } finally {
      setSending(false)
    }
  }

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-4">
      <form
        onSubmit={submit}
        className="flex max-h-[90vh] w-full max-w-lg flex-col rounded-lg bg-white shadow-xl dark:bg-gray-800"
      >
        <div className="flex items-center justify-between border-b px-5 py-3 dark:border-gray-700">
          <h3 className="font-semibold text-gray-900 dark:text-gray-100">New pod message</h3>
          <button type="button" onClick={onClose} aria-label="Close" className="p-1 text-gray-400">
            <X className="h-5 w-5" />
          </button>
        </div>

        <div className="flex-1 space-y-4 overflow-y-auto px-5 py-4">
          <div>
            <label
              htmlFor="pod-scope"
              className="mb-1 block text-sm font-medium text-gray-700 dark:text-gray-300"
            >
              Pod
            </label>
            <select
              id="pod-scope"
              value={scope}
              onChange={(e) => setScope(e.target.value)}
              required
              className="w-full rounded border px-2 py-1.5 text-sm dark:border-gray-600 dark:bg-gray-900"
            >
              <option value="">Choose a pod</option>
              {pods.map((p) => (
                <option key={p.id} value={p.id}>
                  {p.name}
                </option>
              ))}
            </select>
          </div>

          <fieldset disabled={!scope}>
            <legend className="mb-1 text-sm font-medium text-gray-700 dark:text-gray-300">
              Send to
            </legend>
            <div className="flex flex-wrap gap-3 text-sm">
              {[
                { value: 'cohort', label: 'Whole cohort' },
                { value: 'group', label: 'A group' },
                // Only offered when the pod actually owns classes; otherwise it would always fail.
                ...(classes.length > 0 ? [{ value: 'class', label: 'A class' }] : []),
                { value: 'students', label: 'Specific students' }
              ].map((opt) => (
                <label key={opt.value} className="flex items-center gap-1">
                  <input
                    type="radio"
                    name="pod-audience"
                    value={opt.value}
                    checked={audienceType === opt.value}
                    onChange={() => setAudienceType(opt.value)}
                  />
                  {opt.label}
                </label>
              ))}
            </div>

            {audienceType === 'group' && (
              <select
                value={groupId}
                onChange={(e) => setGroupId(e.target.value)}
                required
                aria-label="Choose a group"
                className="mt-2 w-full rounded border px-2 py-1.5 text-sm dark:border-gray-600 dark:bg-gray-900"
              >
                <option value="">Choose a group</option>
                {groups.map((g) => (
                  <option key={g.id} value={g.id}>
                    {g.name} ({g.members.filter((m) => m.status === 'active').length} active)
                  </option>
                ))}
              </select>
            )}

            {audienceType === 'class' && (
              <>
                <select
                  value={classId}
                  onChange={(e) => setClassId(e.target.value)}
                  required
                  aria-label="Choose a class"
                  className="mt-2 w-full rounded border px-2 py-1.5 text-sm dark:border-gray-600 dark:bg-gray-900"
                >
                  <option value="">Choose a class</option>
                  {classes.map((c) => (
                    <option key={c.id} value={c.id}>
                      {c.name}
                    </option>
                  ))}
                </select>
                <p className="mt-1 text-xs text-gray-500 dark:text-gray-400">
                  Reaches students who are in this class <em>and</em> enrolled with consent in this
                  pod.
                </p>
              </>
            )}

            {audienceType === 'students' && (
              <div className="mt-2 flex max-h-32 flex-wrap gap-2 overflow-y-auto rounded border p-2 dark:border-gray-600">
                {students.length === 0 ? (
                  <span className="text-xs text-gray-400">No enrolled students yet.</span>
                ) : (
                  students.map((s) => (
                    <label key={s.id} className="flex items-center gap-1 text-xs">
                      <input
                        type="checkbox"
                        checked={targetIds.includes(s.id)}
                        onChange={() => toggleTarget(s.id)}
                      />
                      {s.name}
                    </label>
                  ))
                )}
              </div>
            )}
          </fieldset>

          <input
            type="text"
            value={subject}
            onChange={(e) => setSubject(e.target.value)}
            maxLength={200}
            aria-label="Subject"
            placeholder="Subject (optional)"
            className="w-full rounded border px-3 py-2 text-sm dark:border-gray-600 dark:bg-gray-900"
          />
          <textarea
            value={content}
            onChange={(e) => setContent(e.target.value)}
            rows={4}
            required
            maxLength={10000}
            aria-label="Message"
            placeholder="Write your message"
            className="w-full rounded border px-3 py-2 text-sm dark:border-gray-600 dark:bg-gray-900"
          />

          <p className="flex items-start gap-1.5 text-xs text-gray-500 dark:text-gray-400">
            <Eye className="mt-0.5 h-3.5 w-3.5 shrink-0" />
            <span>
              Every tutor in the pod can see this conversation, and recipients are emailed a
              notification with the subject only.
            </span>
          </p>
        </div>

        <div className="flex justify-end gap-2 border-t px-5 py-3 dark:border-gray-700">
          <button type="button" onClick={onClose} className="rounded border px-3 py-1.5 text-sm">
            Cancel
          </button>
          <button
            type="submit"
            disabled={!canSend}
            className="inline-flex items-center gap-1 rounded bg-indigo-600 px-3 py-1.5 text-sm font-medium text-white disabled:opacity-50"
          >
            <Send className="h-4 w-4" />
            {sending ? 'Sending' : 'Send'}
          </button>
        </div>
      </form>
    </div>
  )
}
